import { createSelector } from '@reduxjs/toolkit';
import { RootState } from '@algoan/dashboard/store/store';
import {
  Account,
  AccountType,
  AccountWithIndex,
  AccountWithRealBalance,
  AsyncStatus,
  CheckingAccount,
} from '@algoan/dashboard/model';
import { CheckedAccount } from '@algoan/dashboard/store/slice';
import { formatAccountToAccountWithRealBalance } from '@algoan/dashboard/store/utils';
import {
  filterCheckingAndSavingsAccounts,
  filterOutIgnoredAccounts,
  filterOutComputedAccounts,
} from '@algoan/dashboard/utils';
import { analysisBoundsSelector } from './analysis-selectors';

export const accountsFetchingStatusSelector = (state: RootState): AsyncStatus => state.account.accountsFetchingStatus;

export const isAccountsLoadingSelector: (state: RootState) => boolean = createSelector(
  accountsFetchingStatusSelector,
  (accountsFetchingStatus: AsyncStatus): boolean =>
    accountsFetchingStatus === AsyncStatus.IDLE || accountsFetchingStatus === AsyncStatus.PENDING,
);

export const accountsSelector: (state: RootState) => AccountWithRealBalance[] = createSelector(
  (state: RootState): Account[] => state.account.accounts,
  analysisBoundsSelector,
  (accounts, aggregationBounds): AccountWithRealBalance[] => {
    const lastTransactionDate =
      Array.isArray(aggregationBounds) && aggregationBounds.length === 2 ? aggregationBounds[1] : undefined;

    return accounts.map((account) => {
      return formatAccountToAccountWithRealBalance(account, lastTransactionDate);
    });
  },
);

export const ignoredAccountsSelector: (state: RootState) => AccountWithRealBalance[] = createSelector(
  (state: RootState): Account[] => state.account.ignoredAccounts,
  analysisBoundsSelector,
  (accounts, aggregationBounds): AccountWithRealBalance[] => {
    const lastTransactionDate =
      Array.isArray(aggregationBounds) && aggregationBounds.length === 2 ? aggregationBounds[1] : undefined;

    return accounts.map((account) => {
      return formatAccountToAccountWithRealBalance(account, lastTransactionDate);
    });
  },
);

export const allAccountsWithIndexSelector: (state: RootState) => AccountWithIndex[] = createSelector(
  accountsSelector,
  ignoredAccountsSelector,
  (accounts, ignoredAccounts): AccountWithIndex[] => {
    const allAccounts = [...accounts, ...ignoredAccounts].sort((accountA, accountB) =>
      accountA.originalIndex && accountB.originalIndex ? accountA.originalIndex - accountB.originalIndex : 0,
    );

    const accountsIndex: Record<AccountType, number> = {
      [AccountType.CHECKING]: 1,
      [AccountType.SAVINGS]: 1,
      [AccountType.LOAN]: 1,
      [AccountType.CREDIT_CARD]: 1,
      [AccountType.UNKNOWN]: 1,
    };

    return allAccounts.map((account) => ({ ...account, index: accountsIndex[account.type]++ }));
  },
);

export const accountsWithIndexSelector: (state: RootState) => AccountWithIndex[] = createSelector(
  allAccountsWithIndexSelector,
  (allAccounts): AccountWithIndex[] => {
    return filterOutIgnoredAccounts(allAccounts);
  },
);

export const ignoredAccountsWithIndexSelector: (state: RootState) => AccountWithIndex[] = createSelector(
  allAccountsWithIndexSelector,
  (allAccounts): AccountWithIndex[] => {
    return filterOutComputedAccounts(allAccounts);
  },
);

export const checkingAndSavingsAccountsSelector: (state: RootState) => CheckingAccount[] = createSelector(
  accountsWithIndexSelector,
  (accounts): CheckingAccount[] => filterCheckingAndSavingsAccounts(accounts),
);

export const checkedAccountsSelector = (state: RootState): CheckedAccount[] => state.account.checkedAccounts;
